import { useDialog } from '@repo/ui';
import { AuthGuard } from '@repo/user-session';
import { Provider as JotaiProvider, useAtom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { PreloadedQuery, usePreloadedQuery, useQueryLoader, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';

import { initSurveyQuery } from '@/__generated__/initSurveyQuery.graphql';
import { initSurveyRefundHistoryIdQuery } from '@/__generated__/initSurveyRefundHistoryIdQuery.graphql';
import Spinner from '@/components/common/Spinner';
import { refundHistoryIdQuery, surveyInitQuery } from '@/graphql/query/survey/init';
import { COMMON_PATHS, SURVEY_PAGES } from '@/lib/constants/paths';
import {
  employeesAtom,
  refundHistoryIdAtom,
  someCompletedBusinessSurveyAtom,
  surveyStatusAtom,
  businessesAtom,
  currentBusinessBmanTinAtom,
  imputedYearAtom
} from '@/lib/stores/survey/common';
import { EmployeeType } from '@/lib/types/survey';

export const SurveyLayout = (page: ReactElement) => <SurveyProvider>{page}</SurveyProvider>;
export const SurveyProvider = ({ children }: { children: ReactNode }) => (
  <AuthGuard>
    <JotaiProvider>
      <SurveyInitProvider>{children}</SurveyInitProvider>
    </JotaiProvider>
  </AuthGuard>
);

const SurveyInitProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const relayEnv = useRelayEnvironment();
  const [queryRef, loadQuery] = useQueryLoader<initSurveyQuery>(surveyInitQuery);
  const [refundHistoryId, setRefundHistoryId] = useAtom(refundHistoryIdAtom);
  const { showDialog } = useDialog();
  const isInit = useRef(false);

  useEffect(() => {
    const init = async () => {
      isInit.current = true;
      try {
        const res = await fetchQuery<initSurveyRefundHistoryIdQuery>(relayEnv, refundHistoryIdQuery, {}).toPromise();
        if (res?.getRefundHistoryId.refundHistoryId) {
          const refundHistoryId = res.getRefundHistoryId.refundHistoryId;
          setRefundHistoryId(refundHistoryId);
          loadQuery({ refundHistoryId });
        }
      } catch {
        showDialog({
          title: '설문 정보가 유효하지 않거나,\n설문의 대상자가 아닙니다.',
          buttons: {
            primary: {
              text: '확인',
              onClick: () => router.push(COMMON_PATHS.환급조회목록)
            }
          }
        });
        return;
      }
    };

    if (isInit.current) return;
    if (refundHistoryId) return;
    init();
  }, [relayEnv, refundHistoryId, loadQuery, setRefundHistoryId, router, showDialog]);

  if (!queryRef) return <Spinner />;
  return <InitSurveyAtomProvider queryRef={queryRef}>{children}</InitSurveyAtomProvider>;
};

const InitSurveyAtomProvider = ({ queryRef, children }: { queryRef: PreloadedQuery<initSurveyQuery>; children: ReactNode }) => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const { getSurveyStatus, getSurveyEmployees, getSurveyBusinessAnswer, getSurveyBusinessInfo, bznavRefund } = usePreloadedQuery<initSurveyQuery>(
    surveyInitQuery,
    queryRef
  );
  const isInit = useRef(false);
  const setSurveyStatus = useSetAtom(surveyStatusAtom);
  const setEmployees = useSetAtom(employeesAtom);
  const setSomeCompletedBusinessSurvey = useSetAtom(someCompletedBusinessSurveyAtom);
  const setBusinesses = useSetAtom(businessesAtom);
  const setImputedYear = useSetAtom(imputedYearAtom);
  const [currentBmanTin, setCurrentBusinessBmanTin] = useAtom(currentBusinessBmanTinAtom);

  const imputedYear = bznavRefund.imputedYear.indis;
  const errorCode = getSurveyStatus.errors?.code;
  const surveyStatus = getSurveyStatus.surveyStatus;
  const employees = getSurveyEmployees?.employees;
  const businessAnswer = getSurveyBusinessAnswer?.businessAnswer;
  const businessInfo = getSurveyBusinessInfo?.business;

  useEffect(() => {
    if (isInit.current) return;
    if (currentBmanTin) return;
    // 만료 또는 설문완료 or 기타에러
    isInit.current = true;
    if (errorCode) {
      if (errorCode === 'ERR_SURVEY_COMPLETED') {
        router.replace(SURVEY_PAGES.설문완료);
        return;
      }
      if (errorCode === 'ERR_SURVEY_EXPIRED') {
        router.replace(SURVEY_PAGES.만료);
        return;
      }
      // 만료 또는 설문완료 에러가 아니면 설문을 진행할 수 없으므로 환급홈 이동
      showDialog({
        title: '일시적인 오류가 발생했어요.\n잠시 후 다시 시도해주세요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => router.push(COMMON_PATHS.환급조회목록)
          }
        }
      });
      return;
    }

    if (imputedYear) {
      setImputedYear(imputedYear);
    }
    if (surveyStatus) {
      const { isCompleted, expiredAt, surveyAnswerId, deductionTypes, totalAmount, txprNm } = surveyStatus;
      setSurveyStatus({
        surveyAnswerId: surveyAnswerId ?? '',
        expiredAt: expiredAt ?? new Date().toString(),
        deductionTypes: deductionTypes as string[],
        totalAmount: totalAmount ?? 0,
        isCompleted: isCompleted ?? false,
        taxpayerName: txprNm ?? ''
      });
    }

    if (employees?.length) {
      const newEmployees: EmployeeType[] =
        employees?.map((person) => ({
          id: person.indvTin,
          name: person.ieNm,
          birthday: person.birthday ?? '',
          workedYears: person.attrYears.slice()
        })) || [];
      setEmployees(newEmployees);
    }

    if (businessAnswer?.length && businessInfo?.length) {
      setSomeCompletedBusinessSurvey(businessAnswer?.some(({ isCompleted }) => !!isCompleted));
      const orderedBusinesses = businessAnswer
        .map((businessAnswer) => {
          const targetBusinessInfo = businessInfo?.find((info) => info.bmanTin === businessAnswer.bmanTin) || {
            bmanTin: businessAnswer.bmanTin,
            totalAmount: 0
          };
          return {
            bmanTin: businessAnswer.bmanTin || '',
            totalAmount: targetBusinessInfo?.totalAmount || 0,
            isCompleted: businessAnswer.isCompleted || false
          };
        })
        .sort((a, b) => {
          if (a.isCompleted !== b.isCompleted) {
            return a.isCompleted ? 1 : -1;
          }
          return b?.totalAmount - a?.totalAmount;
        });

      setBusinesses(orderedBusinesses);
      setCurrentBusinessBmanTin(orderedBusinesses[0]?.bmanTin || '');
    }
  }, [
    businessInfo,
    businessAnswer,
    employees,
    imputedYear,
    surveyStatus,
    currentBmanTin,
    errorCode,
    router,
    showDialog,
    setBusinesses,
    setCurrentBusinessBmanTin,
    setEmployees,
    setImputedYear,
    setSomeCompletedBusinessSurvey,
    setSurveyStatus
  ]);

  return <>{children}</>;
};
